// src/utils/pushToken.js
import axios from "axios";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../firebase";

const API = "https://bhavanaastro.onrender.com";

export const isInApp = () =>
  typeof window !== "undefined" && !!window.ReactNativeWebView;

const getStoredUser = () => {
  try {
    return (
      JSON.parse(sessionStorage.getItem("user")) ||
      JSON.parse(localStorage.getItem("user")) ||
      null
    );
  } catch {
    return null;
  }
};

const authHeader = (userData) => ({
  headers: { Authorization: `Bearer ${userData.token}` },
});

// ---------------- Inside the phone app ----------------
// The app injects the token, sometimes after the page has loaded
function waitForExpoToken(ms = 15000) {
  return new Promise((resolve) => {
    if (window.EXPO_PUSH_TOKEN) return resolve(window.EXPO_PUSH_TOKEN);

    let timer;
    const finish = (t) => {
      clearTimeout(timer);
      window.removeEventListener("expoPushToken", onEvt);
      resolve(t);
    };
    const onEvt = (e) => finish(e.detail);

    timer = setTimeout(() => finish(null), ms);
    window.addEventListener("expoPushToken", onEvt);

    // In case the app sent it before we started listening, ask again
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "REQUEST_PUSH_TOKEN" })
    );
  });
}

async function registerExpoPushToken(userData) {
  const token = await waitForExpoToken();
  if (!token) return;
  await axios.post(`${API}/api/save-expo-token`, { token }, authHeader(userData));
}

// ---------------- Normal browser (web push) ----------------
async function registerWebPush(userData) {
  if (!messaging || !("Notification" in window) || !("serviceWorker" in navigator)) return;
  if (Notification.permission === "denied") return;

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") return;

  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  const token = await getToken(messaging, {
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return;

  await axios.post(`${API}/api/save-fcm-token`, { token }, authHeader(userData));
}

// ---------------- Public helpers ----------------

// Call after login, and on app start when already logged in. Never throws.
export async function registerPushForUser(userData = getStoredUser()) {
  try {
    if (!userData?.token || !["user", "astrologer"].includes(userData.role)) return;
    if (isInApp()) await registerExpoPushToken(userData);
    else await registerWebPush(userData);
  } catch (err) {
    console.error("Push registration failed:", err);
  }
}

// Call on logout BEFORE the session is cleared. Never throws.
export async function unregisterPush(userData = getStoredUser()) {
  try {
    if (!userData?.token) return;
    await axios.post(
      `${API}/api/remove-push-token`,
      { type: isInApp() ? "expo" : "fcm" },
      authHeader(userData)
    );
  } catch (err) {
    console.error("Push unregister failed:", err);
  }
}

// Shows a notification while the website is open in a normal browser.
// Returns a cleanup function. Does nothing inside the app (the app shows its own banner).
export function listenForegroundMessages() {
  if (!messaging || isInApp()) return () => {};
  try {
    return onMessage(messaging, (payload) => {
      const title = payload.notification?.title || "New Notification";
      const body = payload.notification?.body || "You have a new message";
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body });
      }
    });
  } catch (err) {
    console.warn("Foreground push listener failed:", err);
    return () => {};
  }
}